"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { safeGet, safeRemove, safeSet } from "@/components/onboarding/safe-storage";
import { getUserScopedKey } from "@/lib/user-scoped-storage";
import { coerceStoredEntered, ENTERED_STORAGE_KEY, liveEntered, type EnteredSlip } from "@/lib/fantasy-props/best-slips";

export type EnteredSlips = {
  readonly entered: readonly EnteredSlip[];
  readonly add: (slip: Omit<EnteredSlip, "enteredAtMs">) => void;
  readonly remove: (id: string) => void;
};

export function useEnteredSlips({ userId, book, nowMs }: { userId: string | undefined; book: string | null; nowMs: number }): EnteredSlips {
  const storageKey = userId && book !== null ? getUserScopedKey(userId, `${ENTERED_STORAGE_KEY}:${book}`) : null;
  const [all, setAll] = useState<EnteredSlip[]>([]);
  const hydratedKeyRef = useRef<string | null>(null);
  const nowRef = useRef(nowMs);
  nowRef.current = nowMs;

  useEffect(() => {
    if (storageKey === null) {
      hydratedKeyRef.current = null;
      setAll([]);
      return;
    }
    if (hydratedKeyRef.current === storageKey) return;
    hydratedKeyRef.current = storageKey;
    const raw = safeGet(storageKey);
    let restored: EnteredSlip[] = [];
    if (raw) {
      try {
        restored = coerceStoredEntered(JSON.parse(raw));
      } catch {
        restored = [];
      }
    }
    setAll(liveEntered(restored, nowRef.current));
  }, [storageKey]);

  useEffect(() => {
    if (storageKey === null || hydratedKeyRef.current !== storageKey) return;
    if (all.length === 0) {
      safeRemove(storageKey);
      return;
    }
    safeSet(storageKey, JSON.stringify({ v: 1, slips: all }));
  }, [storageKey, all]);

  const entered = useMemo(() => liveEntered(all, nowMs), [all, nowMs]);

  const add = useCallback((slip: Omit<EnteredSlip, "enteredAtMs">) => {
    setAll((current) => (current.some((entry) => entry.id === slip.id) ? current : [...current, { ...slip, enteredAtMs: Date.now() }]));
  }, []);

  const remove = useCallback((id: string) => {
    setAll((current) => current.filter((entry) => entry.id !== id));
  }, []);

  return { entered, add, remove };
}
